import React, { FunctionComponent, useEffect, useState } from 'react';
import GroupHeader from '../TargetGroupView/GroupHeader/GroupHeader';
import AddParticipantsTable from './AddParticipantsTable/AddParticipantsTable';
import { AuthenticatedUserProps } from '../../hoc/WithAuthLayout';
import { useGetGroupQuery } from '../../store/api/groups';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCompanyQuery } from '../../store/api/companies';
import { getGroupPath } from '../../routing-utils';
import {
    AddParticipantInput,
    useAddParticipantMutation,
} from '../../store/api/participants';
import { AddIcon } from 'evergreen-ui';
import DialogComponent from '../../components/Dialog/Dialog';
import styles from './AddParticipantsView.module.css';
import ProgressSpinner from '../../components/ProgressSpinner/ProgressSpinner';

interface AddParticipantsViewProps extends AuthenticatedUserProps {}

export type AddParticipantsTableState = {
    id: any;
    email: string;
    first_name: string;
    last_name: string;
}[];

const AddParticipantsView: FunctionComponent<AddParticipantsViewProps> = (
    props,
) => {
    const { userRoles, hasMultipleCompanies } = props;
    const { companySlug, groupId } = useParams();
    const navigate = useNavigate();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

    const initialUserState = [
        { id: 1, email: '', first_name: '', last_name: '' },
        { id: 2, email: '', first_name: '', last_name: '' },
        { id: 3, email: '', first_name: '', last_name: '' },
        { id: 4, email: '', first_name: '', last_name: '' },
        { id: 5, email: '', first_name: '', last_name: '' },
    ];

    const [addParticipantsTableState, setAddParticipantsTableState] =
        useState<AddParticipantsTableState>(initialUserState);

    const [addParticipant, { isLoading }] = useAddParticipantMutation();

    const isSuperAdmin = userRoles.includes('super-admin');

    const { data: group } = useGetGroupQuery({
        companyIdentifier: companySlug as string,
        groupId: groupId as string,
    });

    const [error, setError] = useState<{
        status: number;
        data: { error: { message: string } };
    }>();

    const { data: company } = useGetCompanyQuery(companySlug as string);

    useEffect(() => {
        if (group?.type === 'MOTIVIEW' && companySlug && groupId) {
            navigate(getGroupPath(companySlug, groupId), { replace: true });
        }
    }, [group?.type, companySlug, groupId, navigate]);

    const emailRegEx =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!group || !company) {
        return <></>;
    }

    if (group.type === 'MOTIVIEW') {
        return <></>;
    }

    const isTargetGroupPublic = group.config?.is_public
        ? group.config.is_public
        : false;

    const onAddParticipants = (participants: AddParticipantInput[]) => {
        addParticipant({
            companyIdentifier: company.id,
            groupId: group.id,
            participantsToAdd: participants,
        })
            .unwrap()
            .then(() => {
                navigate(getGroupPath(company.slug, group.id));
            })
            .catch((error) => {
                setError(error);
            });
    };

    const usersInArray = Object.keys(addParticipantsTableState)
        .map((userIndex) => {
            const user = addParticipantsTableState[parseInt(userIndex)];
            return {
                email: user.email,
                first_name: user.first_name,
                last_name: user.last_name,
            };
        })
        .filter((user) => {
            return !!(
                user &&
                user.email &&
                user.email !== '' &&
                emailRegEx.test(user.email) &&
                user.first_name &&
                user.first_name !== '' &&
                user.last_name &&
                user.last_name !== ''
            );
        });

    const rowsWithData = Object.keys(addParticipantsTableState)
        .map((userIndex) => {
            const user = addParticipantsTableState[parseInt(userIndex)];
            return {
                email: user.email,
                first_name: user.first_name,
                last_name: user.last_name,
            };
        })
        .filter((user) => {
            return !!(
                (user && user.email && user.email !== '') ||
                (user.first_name && user.first_name !== '') ||
                (user.last_name && user.last_name !== '')
            );
        });

    const isUserDataMissing =
        usersInArray.length === 0 ||
        usersInArray.length !== rowsWithData.length;

    // @ts-ignore
    return (
        <div>
            <GroupHeader
                isSuperAdmin={isSuperAdmin}
                hasMultipleCompanies={hasMultipleCompanies}
                groupName={group.name}
                companyName={company.name}
                companySlug={company.slug}
                participantsCount={group.participants_count}
                groupType={group.type}
                groupId={group.id}
                companyId={company.id}
                isTargetGroupPublic={isTargetGroupPublic}
                showSettings={false}
                showAddParticipants={false}
                showDownloadFeedback={false}
                showDownloadScores={false}
            />
            <AddParticipantsTable
                onCancel={() => navigate(getGroupPath(company.slug, group.id))}
                setShowConfirmModal={setShowConfirmModal}
                addParticipantsTableState={addParticipantsTableState}
                setAddParticipantsTableState={setAddParticipantsTableState}
                isUserDataMissing={isUserDataMissing}
                emailRegEx={emailRegEx}
            />
            <DialogComponent
                title="Confirm to create candidates"
                confirmButtonText="Create candidates"
                isShown={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={() => onAddParticipants(usersInArray)}
                confirmIcon={AddIcon}
                buttonColor={isUserDataMissing ? 'grayDisabled' : 'tealBlue'}
                isConfirmButtonDisabled={isUserDataMissing || isLoading}>
                <div className={styles.dialog__content}>
                    {usersInArray.length > 0 ? (
                        <>
                            <div>
                                Do you really want to create the following
                                candidate(s)?
                                <br />
                                When you confirm, the candidate(s) will{' '}
                                <b>automatically</b> receive an email to verify
                                their account and set a new password. Then they
                                will receive an invitation email to complete the
                                scan.
                                <table>
                                    <tbody>
                                        {usersInArray.map((user) => {
                                            return (
                                                <tr
                                                    key={`confirm+${user.email}`}>
                                                    <td>
                                                        <li
                                                            className={
                                                                styles.dialog__list
                                                            }>
                                                            {user.first_name}{' '}
                                                            {user.last_name}
                                                        </li>
                                                    </td>
                                                    <td>{user.email}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {isLoading && (
                                <div className={styles.loading}>
                                    <ProgressSpinner />
                                </div>
                            )}
                        </>
                    ) : (
                        'Candidate names and/or email addresses are missing. '
                    )}

                    {!!error && (
                        <div>
                            <p className={styles.error}>
                                {`Error: ${error.status} - ${error.data.error.message}`}
                            </p>
                        </div>
                    )}
                </div>
            </DialogComponent>
        </div>
    );
};

export default AddParticipantsView;
